import React, { useEffect, useState } from "react";
import classNames from "classnames";
import moment from "moment";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highstock";
import { Column, Row } from "components/Layout";
import Checkbox from "components/Checkbox";
import styles from "./_charts.module.scss";


const turboThreshold = 10000;

const baseParameters = [
	'externalHumidity',
	'externalHumidity2',
	'externalTemperature',
	'externalTemperature2',
	'soilTemperature',
	'soilTemperature2',
	'soilHumidity',
	'soilHumidity2',
	'leafHumidity',
	'pluviometer',
	'radiation',
	'windSpeed',
	'atmPress',
]

const filters = {
	'main': a => [...baseParameters, 'absoluteDistance', 'relativeDistance'].includes(a.id),
	'forecast': a => ['externalTemperature', 'externalHumidity', 'atmPress', 'windSpeed', 'pluviometer'].includes(a.id),
	'freezing': a => ['dewPoint', 'dewPoint2', 'wetBulbTemperature', 'wetBulbTemperature2', 'externalTemperature', 'externalTemperature2', 'externalHumidity', 'externalHumidity2', 'deltaTemperature', 'deltaTemperature2'].includes(a.id),
	'irrigation': a => ['pluviometer', 'soilHumidity', 'soilHumidity2', 'externalTemperature', 'externalTemperature2', 'externalHumidity', 'externalHumidity2', 'radiation', 'windSpeed', 'vaporPressureDeficit', 'vaporPressureDeficit2', 'deltaTemperature', 'deltaTemperature2'].includes(a.id),
	'phytosanitary': a => ['pluviometer', 'externalTemperature', 'externalTemperature2', 'externalHumidity', 'externalHumidity2', 'leafHumidity', 'windSpeed'].includes(a.id),
	'solar': a => ['radiation', 'externalTemperature', 'inverter_energy'].includes(a.id),
	'trap': a => ['insectCount1', 'insectCount2'].includes(a.id),
	// statistics and adversities charts
	'stats': a => [...baseParameters, 'evapotraspirazioneArboree', 'evapotraspirazioneErbacee'].includes(a.id),
	'olivo-cicloconio': a => ['externalTemperature', 'externalHumidity', 'leafHumidity', 'risk'].includes(a.id),
	'melo-ticchiolatura': a => ['externalTemperature', 'externalHumidity', 'leafHumidity', 'ticchiolaturaDegreeHour'].includes(a.id),
	'stats-solar': a => ['radiatedEnergy', 'externalTemperature', 'inverter_energy', 'pvgis_energy'].includes(a.id),
}

const statTypes = ['stats', 'olivo-cicloconio', 'melo-ticchiolatura', 'stats-solar']


function createAxis({ id, text, format, color, ...otherProps }) {
	let plotLines = null
	if (id === 'axis-temperature') {
		plotLines = [{
			value: 0,
			color,
			width: 1,
			zIndex: 4,
			label: { text: "0°C", style: { color } }
		}]
	}

	return {
		id,
		gridLineWidth: 0,
		color,
		title: {
			style: { color },
			text
		},
		labels: {
			style: { color },
			format
		},
		plotLines,
		showLastLabel: false,
		visible: true,
		...otherProps
	};
}

function axisIndexById(id) {
	return chartAxes.findIndex(a => a.id === id)
}

let chartAxes = [
	{
		id: 'axis-humidity',
		text: "Umidità'",
		format: "{value}%",
		opposite: true,
		min: 0,
		max: 100,
		color: '#00BCD4',
	},
	{
		id: 'axis-temperature',
		text: "Temperatura",
		format: "{value}°C",
		opposite: true,
		color: '#B71C1C',
	},
	{
		id: 'axis-waterPotential',
		text: "Potenziale Idrico",
		format: "{value} cb",
		opposite: true,
		color: '#808900',
		reversed: true,
		min: 0,
	},
	{
		id: "axis-leafHumidity",
		label: "BAGNATURA FOGLIARE",
		text: "Bagnatura Fogliare",
		format: "{value}%",
		opposite: false,
		min: 0,
		max: 100,
		color: '#8BC34A',
	},
	{
		id: "axis-rainfall",
		text: "Pioggia",
		format: "{value} mm",
		opposite: false,
		color: '#0d47a1',
	},
	{
		id: "axis-radiation",
		text: "Radiazione Solare",
		format: "{value} W/mq",
		opposite: false,
		color: '#ffc107',
	},
	{
		id: 'axis-windSpeed',
		text: "vento",
		format: "{value} km/h",
		opposite: false,
		color: '#9b9b9b',
	},
	{
		id: "axis-atmPress",
		text: "pressione",
		format: "{value} hPa",
		opposite: false,
		color: '#000000',
	},
	{
		id: "axis-vpd",
		text: "VPD",
		format: "{value} kPa",
		opposite: false,
		color: '#000000',
	},
	{
		id: "axis-et0",
		text: "Evapotraspirazione potenziale (ET0)",
		format: "{value} mm/day",
		opposite: false,
		color: '#000000',
	},
	{
		id: "axis-distance",
		text: "distanza",
		format: "{value} cm",
		opposite: false,
		color: '#9013FE',
	},
	{
		id: "axis-risk",
		text: "Rischio Avversità",
		format: "{value}",
		opposite: false,
		min: 0,
		max: 10,
		color: 'black',
	},
	{
		id: "axis-degreeHour",
		text: "Gradi Ora",
		format: "{value} °ora",
		opposite: false,
		min: 0,
		color: 'black',
	},
	{
		id: "axis-power",
		text: "Potenza",
		format: "{value} kW",
		opposite: false,
		color: '#000000',
	},
	{
		id: "axis-energy",
		text: "Energia",
		format: "{value} kWh",
		opposite: false,
		color: '#000000',
	},
	{
		id: "axis-radEnergy",
		text: "Energia irradiata",
		format: "{value} kWh/mq",
		opposite: false,
		color: '#ffc107',
	},
	{
		id: "axis-currency",
		text: "Profitto",
		format: "{value} €",
		opposite: true,
		color: '#000000',
	},
	{
		id: "axis-count",
		text: "Conteggio",
		format: "{value}",
		opposite: true,
		min: 0,
		color: '#000000',
	},
].map(axis => createAxis(axis))

let chartSeries = [
	{
		id: "externalHumidity",
		label: "UMIDITA' ATMOSFERICA",
		color: '#0097A7',
		type: 1,
		series: {
			name: 'Umidità',
			type: 'spline',
			tooltip: { valueSuffix: '%' },
			yAxis: axisIndexById('axis-humidity'),
		},
	},
	{
		id: "externalHumidity2",
		label: "UMIDITA' ATMOSFERICA 2",
		color: '#4dd0e1',
		type: 2,
		series: {
			name: 'Umidità 2',
			type: 'spline',
			tooltip: { valueSuffix: '%' },
			yAxis: axisIndexById('axis-humidity'),
		},
	},
	{
		id: "externalTemperature",
		label: "TEMPERATURA ESTERNA",
		color: '#B71C1C',
		type: 1,
		series: {
			name: 'Temp. est.',
			type: 'spline',
			tooltip: { valueSuffix: ' °C' },
			yAxis: axisIndexById('axis-temperature'),
		},
	},
	{
		id: "externalTemperature2",
		label: "TEMPERATURA ESTERNA 2",
		color: '#E57373',
		type: 2,
		series: {
			name: 'Temp. est. 2',
			type: 'spline',
			tooltip: { valueSuffix: ' °C' },
			yAxis: axisIndexById('axis-temperature'),
		},
	},
	{
		id: "soilTemperature",
		label: "TEMPERATURA SUOLO 1",
		color: '#ff6f00',
		type: 1,
		series: {
			name: 'Temp. Suolo 1',
			type: 'spline',
			tooltip: { valueSuffix: ' °C' },
			yAxis: axisIndexById('axis-temperature'),
		},
	},
	{
		id: "soilTemperature2",
		label: "TEMPERATURA SUOLO 2",
		color: '#ffa000',
		type: 1,
		series: {
			name: 'Temp. Suolo 2',
			type: 'spline',
			tooltip: { valueSuffix: ' °C' },
			yAxis: axisIndexById('axis-temperature'),
		},
	},
	{
		id: "soilHumidity",
		label: "POTENZIALE IDRICO 1",
		color: '#757d00',
		type: 1,
		series: {
			name: 'Potenziale Idrico 1',
			type: 'spline',
			tooltip: { valueSuffix: ' cb' },
			yAxis: axisIndexById('axis-waterPotential'),
		},
	},
	{
		id: "soilHumidity2",
		label: "POTENZIALE IDRICO 2",
		color: '#b0bc00',
		type: 1,
		series: {
			name: 'Potenziale Idrico 2',
			type: 'spline',
			tooltip: { valueSuffix: ' cb' },
			yAxis: axisIndexById('axis-waterPotential'),
		},
	},
	{
		id: "leafHumidity",
		label: "BAGNATURA FOGLIARE",
		color: '#8BC34A',
		type: 1,
		series: {
			name: 'Umidità Fogl.',
			type: 'spline',
			tooltip: { valueSuffix: ' %' },
			yAxis: axisIndexById('axis-leafHumidity'),
		},
	},
	{
		id: "pluviometer",
		label: "PIOGGIA",
		color: '#0d47a1',
		type: 1,
		series: {
			name: 'Pioggia',
			type: 'spline',
			tooltip: { valueSuffix: ' mm' },
			yAxis: axisIndexById('axis-rainfall'),
		},
	},
	{
		id: "radiation",
		label: "RADIAZIONE SOLARE",
		color: '#ffc107',
		type: 1,
		series: {
			name: 'UV',
			type: 'spline',
			tooltip: { valueSuffix: ' W/mq' },
			yAxis: axisIndexById('axis-radiation'),
		},
	},
	{
		id: "radiatedEnergy",
		label: "ENERGIA SOLARE",
		color: '#ffc107',
		type: 1,
		series: {
			name: 'energia solare',
			type: 'spline',
			tooltip: { valueSuffix: ' kWh/mq' },
			yAxis: axisIndexById('axis-radEnergy'),
		},
	},
	{
		id: "windSpeed",
		label: "VELOCITA' DEL VENTO",
		color: '#9b9b9b',
		type: 2,
		series: {
			name: 'vento',
			type: 'spline',
			tooltip: { valueSuffix: ' km/h' },
			yAxis: axisIndexById('axis-windSpeed'),
		},
	},
	{
		id: "atmPress",
		label: "PRESSIONE ATMOSFERICA",
		color: '#000000',
		type: 2,
		series: {
			name: 'pressione',
			type: 'spline',
			tooltip: { valueSuffix: ' hPa' },
			yAxis: axisIndexById('axis-atmPress'),
		},
	},
	{
		id: "absoluteDistance",
		label: "DISTANZA ASSOLUTA",
		color: '#9013FE',
		type: 2,
		series: {
			name: 'distanza assoluta',
			type: 'spline',
			tooltip: { valueSuffix: ' cm' },
			yAxis: axisIndexById('axis-distance'),
		},
	},
	{
		id: "relativeDistance",
		label: "DISTANZA RELATIVA",
		color: '#BD10E0',
		type: 2,
		series: {
			name: 'distanza relativa',
			type: 'spline',
			tooltip: { valueSuffix: ' cm' },
			yAxis: axisIndexById('axis-distance'),
		},
	},
	{
		id: "dewPoint",
		label: "PUNTO DI RUGIADA",
		color: '#653294',
		series: {
			name: 'Punto Rugiada',
			type: 'spline',
			tooltip: { valueSuffix: ' °C' },
			yAxis: axisIndexById('axis-temperature'),
		},
	},
	{
		id: "dewPoint2",
		label: "PUNTO DI RUGIADA 2",
		color: "#7b64ff",
		series: {
			name: 'Punto Rugiada 2',
			type: 'spline',
			tooltip: { valueSuffix: ' °C' },
			yAxis: axisIndexById('axis-temperature'),
		},
	},
	{
		id: "wetBulbTemperature",
		label: "TEMPERATURA BULBO UMIDO",
		color: '#ab149e',
		series: {
			name: 'Temp. Bulbo Umido',
			type: 'spline',
			tooltip: { valueSuffix: ' °C' },
			yAxis: axisIndexById('axis-temperature'),
		},
	},
	{
		id: "wetBulbTemperature2",
		label: "TEMPERATURA BULBO UMIDO 2",
		color: '#fa28ff',
		series: {
			name: 'Temp. Bulbo Umido 2',
			type: 'spline',
			tooltip: { valueSuffix: ' °C' },
			yAxis: axisIndexById('axis-temperature'),
		},
	},
	{
		id: "deltaTemperature",
		label: "DELTA T",
		color: '#ab8314ff',
		series: {
			name: 'Delta T',
			type: 'spline',
			tooltip: { valueSuffix: ' °C' },
			yAxis: axisIndexById('axis-temperature'),
		},
	},
	{
		id: "deltaTemperature2",
		label: "DELTA T 2",
		color: '#ffa928ff',
		series: {
			name: 'Delta T 2',
			type: 'spline',
			tooltip: { valueSuffix: ' °C' },
			yAxis: axisIndexById('axis-temperature'),
		},
	},
	{
		id: "vaporPressureDeficit",
		label: "DEFICIT DI PRESSIONE DI VAPORE (VPD)",
		color: '#000000',
		series: {
			name: 'VPD',
			type: 'spline',
			tooltip: { valueSuffix: ' kPa' },
			yAxis: axisIndexById('axis-vpd'),
		},
	},
	{
		id: "vaporPressureDeficit2",
		label: "DEFICIT DI PRESSIONE DI VAPORE (VPD) 2",
		color: '#666666',
		series: {
			name: 'VPD 2',
			type: 'spline',
			tooltip: { valueSuffix: ' kPa' },
			yAxis: axisIndexById('axis-vpd'),
		},
	},
	{
		id: "evapotraspirazioneArboree",
		label: "EVAPOTRASPIRAZIONE POTENZIALE ARBOREE (ET0)",
		color: '#000000',
		series: {
			name: 'ET0 arboree',
			type: 'spline',
			tooltip: { valueSuffix: ' mm/day' },
			yAxis: axisIndexById('axis-et0'),
		},
	},
	{
		id: "evapotraspirazioneErbacee",
		label: "EVAPOTRASPIRAZIONE POTENZIALE ERBACEE (ET0)",
		color: '#666666',
		series: {
			name: 'ET0 erbacee',
			type: 'spline',
			tooltip: { valueSuffix: ' mm/day' },
			yAxis: axisIndexById('axis-et0'),
		},
	},
	{
		id: "risk",
		label: "RISCHIO AVVERSITA'",
		color: '#000000',
		series: {
			name: 'Rischio Avversità',
			type: 'column',
			tooltip: { valueSuffix: '/10' },
			yAxis: axisIndexById('axis-risk'),
			zoneAxis: 'y',
			zones: [
				{ value: 0, color: 'green' },
				{ value: 3, color: 'green' },
				{ value: 6, color: 'orange' },
				{ color: 'red' },
			],
		},
	},
	{
		id: "ticchiolaturaDegreeHour",
		label: "RISCHIO AVVERSITA'",
		color: '#000000',
		series: {
			name: 'Rischio Avversità',
			type: 'column',
			tooltip: { valueSuffix: ' °ora' },
			yAxis: axisIndexById('axis-degreeHour'),
			zoneAxis: 'y',
			zones: [
				{ value: 0, color: 'green' },
				{ value: 130, color: 'green' },
				{ value: 150, color: 'yellow' },
				{ value: 180, color: 'orange' },
				{ color: 'red' },
			],
		},
	},
	// solar plant data
	{
		id: "inverter_energy",
		label: "Energia prodotta",
		color: '#4dd0e1',
		series: {
			name: "Energia prodotta",
			type: 'spline',
			tooltip: { valueSuffix: ' kWh' },
			yAxis: axisIndexById('axis-energy'),
		},
	},
	{
		id: "pvgis_energy",
		label: "Produzione PVGIS",
		color: '#000000',
		series: {
			name: 'Produzione PVGIS',
			type: 'spline',
			tooltip: { valueSuffix: ' kWh' },
			yAxis: axisIndexById('axis-energy'),
		},
	},
	// radarpest data
	{
		id: "insectCount1",
		label: "Conteggio insetti 1",
		color: '#8BC34A',
		series: {
			name: 'Cont. insetti 1',
			type: 'spline',
			tooltip: { valueSuffix: '' },
			yAxis: axisIndexById('axis-count'),
		},
	},
	{
		id: "insectCount2",
		label: "Conteggio insetti 2",
		color: '#B71C1C',
		series: {
			name: 'Cont. insetti 2',
			type: 'spline',
			tooltip: { valueSuffix: '' },
			yAxis: axisIndexById('axis-count'),
		},
	},
].map(series => {
	series.series.color = series.color
	series.series.turboThreshold = turboThreshold
	return series
});

let _chartOptions = {
	legend: { enabled: false },
	title: { text: null },
	chart: { zoomType: 'xy' },
	xAxis: { type: 'datetime', gridLineDashStyle: 'dash', gridLineWidth: 1 },
	yAxis: chartAxes,
	tooltip: { shared: true },
	time: { timezone: 'Europe/Rome', useUTC: false },
};


export default function Charts({ data, type }) {
	const [labels, setLabels] = useState([])
	const [selectedLabels, setSelectedLabels] = useState([])
	const [allLabels, setAllLabels] = useState(false)
	const [chartOptions, setChartOptions] = useState(_chartOptions)

	useEffect(() => setLabels(chartSeries.filter(filters[type] || filters['main']).map(a => a.id)), [type])
	useEffect(() => setSelectedLabels(labels.length ? [labels[0]] : []), [labels])
	useEffect(updateChart, [data, selectedLabels, type])

	function updateChart() {
		let chartOptions = Object.assign({}, _chartOptions)
		let series = [];

		chartAxes.forEach(a => a.visible = false)
		// evito che nei grafici statistici la data sia disallineata per via del fuso orario
		chartOptions.time.useUTC = statTypes.includes(type)
		chartSeries.forEach(a => {
			let serie = a.series
			serie.data = data.map(d => ({
				name: moment(d.receiptDate).format(statTypes.includes(type) ? "DD/MM/YYYY" : "DD/MM/YYYY HH:mm"),
				x: d.receiptDate,
				y: d[a.id],
			})).filter(d => !!d.y || d.y === 0)
			const isVisible = selectedLabels.findIndex(sl => sl === a.id) > -1
			serie.visible = isVisible
			if (!serie.zoneAxis) {
				serie.zoneAxis = 'x'
			}
			if (serie.zoneAxis === 'x') {
				serie.zones = [{ value: moment().valueOf() }, { dashStyle: 'dash' }]
			}
			series.push(serie)
			if (isVisible) {
				chartAxes[a.series.yAxis].visible = true
			}
		})
		chartOptions.series = series
		setChartOptions(chartOptions)
	}

	function addLabel(label) {
		let sl = selectedLabels.slice();
		sl.push(label);
		setSelectedLabels(sl);
	}

	function removeLabel(label) {
		let sl = selectedLabels.slice();
		sl = sl.filter(l => l !== label);
		setSelectedLabels(sl);
	}

	function checkAllLabels() {
		let allLabelsNew = !allLabels
		let newSelectedLabels = allLabelsNew? labels.slice(): []
		setAllLabels(allLabelsNew)
		setSelectedLabels(newSelectedLabels)
	}

	return <>
		<Row>
			<Column className={classNames(styles.box, styles.chartContainer)}>
				<HighchartsReact
					highcharts={Highcharts}
					options={chartOptions}
					immutable={true}
				/>
			</Column>
		</Row>
		<Row>
			<Column className={classNames(styles.box, styles.legendContainer)}>
				<div className={styles.legendContainer__header}>
					<div className={styles["legendContainer__header-title"]}>LEGENDA</div>
					<div className={styles["legendContainer__header-subtitle"]}>Scegli quale dato visualizzare</div>
				</div>

				<div className={styles.legendOptions}>
					{labels.map(l =>
						<div className={styles.legendOptions__option} key={`${l}-option`}>
							<Checkbox
								color={"red"}
								onCheck={() => selectedLabels.findIndex(sl => sl === l) < 0 ? addLabel(l) : removeLabel(l)}
								checked={selectedLabels.findIndex(sl => sl === l) !== -1}
							/>
							<div className={styles.legendOptions__label}>
								<div>
									<div className={styles.legendOptions__optionFlag}
										style={{ backgroundColor: chartSeries.find(a => a.id === l).color, marginRight: 15 }} />
								</div>
								{chartSeries.find(a => a.id === l).label}
							</div>
						</div>
					)}
				</div>

				<div className={styles.legendOptions} style={{marginTop: 0}}>
					<div className={styles.legendOptions__option}>
						<Checkbox
							color={"red"}
							onCheck={checkAllLabels}
							checked={allLabels}
						/>
						<div className={styles.legendOptions__label}>
							SELEZIONA TUTTO
						</div>
					</div>
				</div>
			</Column>
		</Row>
	</>
}
